/**
 * X3DH — установление общего секрета между двумя устройствами через
 * опубликованные prekeys (Signal, «The X3DH Key Agreement Protocol», rev. 1).
 *
 * У устройства три пары ключей: подписывающий Ed25519 (identity для людей —
 * из него считается код безопасности), identity X25519 (для DH) и signed
 * prekey X25519, плюс пачка одноразовых prekeys. Подписи Ed25519 связывают
 * X25519-ключи с подписывающим: проверив код безопасности, человек
 * проверяет и все ключи обмена. XEdDSA из спецификации в Web Crypto нет,
 * поэтому подпись — отдельным ключом, а не производной от identity X25519.
 *
 *   DH1 = DH(IK_A, SPK_B)   DH2 = DH(EK_A, IK_B)
 *   DH3 = DH(EK_A, SPK_B)   DH4 = DH(EK_A, OPK_B)   — если одноразовый ключ есть
 *   SK  = HKDF(F ‖ DH1 ‖ DH2 ‖ DH3 [‖ DH4]),  F = 32 байта 0xFF (X25519)
 *   AD  = IK_A ‖ IK_B
 */
import { concat, u32be, utf8 } from './bytes'
import { dh, hkdfSha256, KEY_BYTES, sign, verify, type DhKeyPair, type SigningKeyPair } from './primitives'
import { generateDh } from './primitives'

const INFO_X3DH = utf8('DvizhSecretChat/X3DH/v1')
const SIG_IDENTITY = utf8('DvizhSecretChat/identity/v1')
const SIG_SIGNED_PREKEY = utf8('DvizhSecretChat/spk/v1')
const F = new Uint8Array(KEY_BYTES).fill(0xff)
const ZERO_SALT = new Uint8Array(KEY_BYTES)

/** Долгоживущие ключи этого устройства. */
export interface LocalIdentity {
  signing: SigningKeyPair
  identity: DhKeyPair
}

/** Публичная часть устройства собеседника — то, что отдаёт сервер. */
export interface PrekeyBundle {
  deviceId: string
  signingKey: Uint8Array
  identityKey: Uint8Array
  identitySignature: Uint8Array
  signedPrekey: { id: number; key: Uint8Array; signature: Uint8Array }
  oneTimePrekey: { id: number; key: Uint8Array } | null
}

/** Заголовок первого сообщения сессии: по нему отвечающая сторона выводит тот же SK. */
export interface InitialHeader {
  signingKey: Uint8Array
  identityKey: Uint8Array
  identitySignature: Uint8Array
  ephemeralKey: Uint8Array
  signedPrekeyId: number
  oneTimePrekeyId: number | null
}

export interface EstablishedSecret {
  sharedSecret: Uint8Array
  associatedData: Uint8Array
}

/** Сообщение, которое подписывает identity X25519 подписывающим ключом. */
export function identityMessage(identityKey: Uint8Array): Uint8Array {
  return concat(SIG_IDENTITY, identityKey)
}

export function signedPrekeyMessage(id: number, key: Uint8Array): Uint8Array {
  return concat(SIG_SIGNED_PREKEY, u32be(id), key)
}

export async function signIdentity(me: LocalIdentity): Promise<Uint8Array> {
  return sign(me.signing.privateKey, identityMessage(me.identity.publicKey))
}

export async function signPrekey(me: LocalIdentity, id: number, key: Uint8Array): Promise<Uint8Array> {
  return sign(me.signing.privateKey, signedPrekeyMessage(id, key))
}

/** Обе подписи набора должны сойтись — иначе сервер (или кто-то между) подменил ключи. */
export async function verifyBundle(bundle: PrekeyBundle): Promise<boolean> {
  const identityOk = await verify(
    bundle.signingKey,
    bundle.identitySignature,
    identityMessage(bundle.identityKey),
  )
  if (!identityOk) return false
  return verify(
    bundle.signingKey,
    bundle.signedPrekey.signature,
    signedPrekeyMessage(bundle.signedPrekey.id, bundle.signedPrekey.key),
  )
}

async function deriveShared(parts: Uint8Array[]): Promise<Uint8Array> {
  return hkdfSha256(concat(F, ...parts), ZERO_SALT, INFO_X3DH, KEY_BYTES)
}

/**
 * Сторона-инициатор. Возвращает SK, заголовок для первого сообщения и ключ
 * рэтчета собеседника (его signed prekey — с него начинается Double Ratchet).
 */
export async function initiate(
  me: LocalIdentity,
  bundle: PrekeyBundle,
): Promise<EstablishedSecret & { header: InitialHeader; remoteRatchetKey: Uint8Array }> {
  if (!(await verifyBundle(bundle))) throw new Error('secret_bundle_invalid')

  const ephemeral = await generateDh(true)
  const dh1 = await dh(me.identity.privateKey, bundle.signedPrekey.key)
  const dh2 = await dh(ephemeral.privateKey, bundle.identityKey)
  const dh3 = await dh(ephemeral.privateKey, bundle.signedPrekey.key)
  const parts = [dh1, dh2, dh3]
  if (bundle.oneTimePrekey) parts.push(await dh(ephemeral.privateKey, bundle.oneTimePrekey.key))

  return {
    sharedSecret: await deriveShared(parts),
    associatedData: concat(me.identity.publicKey, bundle.identityKey),
    remoteRatchetKey: bundle.signedPrekey.key,
    header: {
      signingKey: me.signing.publicKey,
      identityKey: me.identity.publicKey,
      identitySignature: await signIdentity(me),
      ephemeralKey: ephemeral.publicKey,
      signedPrekeyId: bundle.signedPrekey.id,
      oneTimePrekeyId: bundle.oneTimePrekey?.id ?? null,
    },
  }
}

/**
 * Отвечающая сторона: по заголовку первого сообщения и своим приватным
 * prekeys выводит тот же SK. Подпись identity инициатора проверяется здесь же —
 * без неё код безопасности ничего бы не гарантировал.
 */
export async function respond(
  // Отвечающей стороне нужен только identity-ключ: подписи здесь не создаются.
  me: Pick<LocalIdentity, 'identity'>,
  signedPrekey: CryptoKey,
  oneTimePrekey: CryptoKey | null,
  header: InitialHeader,
): Promise<EstablishedSecret> {
  const identityOk = await verify(
    header.signingKey,
    header.identitySignature,
    identityMessage(header.identityKey),
  )
  if (!identityOk) throw new Error('secret_bundle_invalid')
  if (header.oneTimePrekeyId !== null && !oneTimePrekey) throw new Error('secret_prekey_missing')

  const dh1 = await dh(signedPrekey, header.identityKey)
  const dh2 = await dh(me.identity.privateKey, header.ephemeralKey)
  const dh3 = await dh(signedPrekey, header.ephemeralKey)
  const parts = [dh1, dh2, dh3]
  if (header.oneTimePrekeyId !== null && oneTimePrekey)
    parts.push(await dh(oneTimePrekey, header.ephemeralKey))

  return {
    sharedSecret: await deriveShared(parts),
    associatedData: concat(header.identityKey, me.identity.publicKey),
  }
}
